/* ============================================================
   BAKKIEBOSS — Ford Landing Page JavaScript
   BUILT FORD TOUGH — Rising forge sparks
   Zero dependencies — vanilla JS only
   ============================================================ */

(function () {
  'use strict';

  /* ==========================================================
     1. FORGE SPARKS (industrial rising embers)
     ========================================================== */
  class ForgeSparks {
    constructor(canvas) {
      this.canvas = canvas;
      this.ctx = canvas.getContext('2d');
      this.particles = [];
      this.raf = null;
      this.paused = false;
      this.isMobile = window.innerWidth <= 768;
      this.resize();
      this.createParticles();
      this.bindEvents();
      this.observeVisibility();
      this.animate();
    }

    resize() {
      const rect = this.canvas.parentElement.getBoundingClientRect();
      this.w = this.canvas.width = rect.width;
      this.h = this.canvas.height = rect.height;
    }

    createParticles() {
      const divisor = this.isMobile ? 25000 : 10000;
      const maxCount = this.isMobile ? 30 : 90;
      const count = Math.min(maxCount, Math.floor((this.w * this.h) / divisor));
      this.particles = [];

      for (let i = 0; i < count; i++) {
        this.particles.push(this.createSpark());
      }
    }

    createSpark() {
      const type = Math.random();
      // Three spark types: blue steel, chrome white, orange ember
      let hue, sat, light;
      if (type < 0.45) {
        // Ford blue sparks
        hue = Math.floor(Math.random() * 20 + 210);     // 210-230 blue
        sat = Math.floor(Math.random() * 30 + 60);       // 60-90%
        light = Math.floor(Math.random() * 20 + 60);     // 60-80%
      } else if (type < 0.75) {
        // Chrome white sparks
        hue = Math.floor(Math.random() * 30 + 200);      // blue-tinted white
        sat = Math.floor(Math.random() * 15 + 5);        // 5-20% (nearly white)
        light = Math.floor(Math.random() * 15 + 80);     // 80-95%
      } else {
        // Orange ember sparks (like welding)
        hue = Math.floor(Math.random() * 20 + 20);       // 20-40 orange
        sat = Math.floor(Math.random() * 20 + 80);       // 80-100%
        light = Math.floor(Math.random() * 20 + 55);     // 55-75%
      }

      return {
        x: Math.random() * this.w,
        y: this.h + Math.random() * 100,          // Start below viewport
        size: Math.random() * 2.5 + 0.3,
        // Rise upward — like sparks from a forge
        speedY: -(Math.random() * 1.2 + 0.3),
        // Slight horizontal drift
        speedX: (Math.random() - 0.5) * 0.6,
        opacity: Math.random() * 0.6 + 0.15,
        maxOpacity: Math.random() * 0.6 + 0.15,
        life: 0,
        maxLife: Math.random() * 200 + 100,        // Frames alive
        hue: hue,
        sat: sat,
        light: light,
        // Flicker effect
        flicker: Math.random() * Math.PI * 2,
        flickerSpeed: Math.random() * 0.15 + 0.05,
        flickerAmp: Math.random() * 0.3 + 0.1,
        // Slight wobble
        wobble: Math.random() * Math.PI * 2,
        wobbleSpeed: Math.random() * 0.03 + 0.01,
        wobbleAmp: Math.random() * 0.5 + 0.2,
      };
    }

    bindEvents() {
      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          this.isMobile = window.innerWidth <= 768;
          this.resize();
          this.createParticles();
        }, 200);
      });
    }

    observeVisibility() {
      const hero = this.canvas.parentElement;
      if (!hero || !('IntersectionObserver' in window)) return;

      const observer = new IntersectionObserver(
        (entries) => {
          this.paused = !entries[0].isIntersecting;
          if (!this.paused && !this.raf) {
            this.animate();
          }
        },
        { threshold: 0 }
      );
      observer.observe(hero);
    }

    animate() {
      if (this.paused) {
        this.raf = null;
        return;
      }

      this.ctx.clearRect(0, 0, this.w, this.h);

      for (let i = 0; i < this.particles.length; i++) {
        const p = this.particles[i];

        // Update life
        p.life++;

        // Fade in at start, fade out at end
        const lifeProgress = p.life / p.maxLife;
        if (lifeProgress < 0.1) {
          p.opacity = p.maxOpacity * (lifeProgress / 0.1);
        } else if (lifeProgress > 0.7) {
          p.opacity = p.maxOpacity * (1 - (lifeProgress - 0.7) / 0.3);
        } else {
          p.opacity = p.maxOpacity;
        }

        // Flicker
        p.flicker += p.flickerSpeed;
        const flickerMod = 1 + Math.sin(p.flicker) * p.flickerAmp;

        // Move upward with wobble
        p.y += p.speedY;
        p.wobble += p.wobbleSpeed;
        p.x += p.speedX + Math.sin(p.wobble) * p.wobbleAmp;

        // Slow down as they rise (like real sparks losing energy)
        p.speedY *= 0.999;

        // Respawn if dead or off-screen
        if (p.life >= p.maxLife || p.y < -20) {
          this.particles[i] = this.createSpark();
          continue;
        }

        // Draw spark with glow
        const alpha = Math.max(0, p.opacity * flickerMod);
        const glowSize = p.size * 3;

        // Outer glow
        this.ctx.beginPath();
        this.ctx.arc(p.x, p.y, glowSize, 0, Math.PI * 2);
        this.ctx.fillStyle = `hsla(${p.hue}, ${p.sat}%, ${p.light}%, ${alpha * 0.15})`;
        this.ctx.fill();

        // Core spark
        this.ctx.beginPath();
        this.ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        this.ctx.fillStyle = `hsla(${p.hue}, ${p.sat}%, ${p.light}%, ${alpha})`;
        this.ctx.fill();
      }

      this.raf = requestAnimationFrame(() => this.animate());
    }

    destroy() {
      cancelAnimationFrame(this.raf);
    }
  }

  /* ==========================================================
     2. SCROLL ANIMATIONS
     ========================================================== */
  function initScrollAnimations() {
    const elements = document.querySelectorAll('[data-animate]');
    if (!elements.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.15,
        rootMargin: '0px 0px -40px 0px',
      }
    );

    elements.forEach((el) => observer.observe(el));
  }

  /* ==========================================================
     3. COUNTER ANIMATION
     ========================================================== */
  function initCounters() {
    const counters = document.querySelectorAll('[data-count]');
    if (!counters.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animateCounter(entry.target);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.5 }
    );

    counters.forEach((el) => observer.observe(el));
  }

  function animateCounter(el) {
    const target = parseInt(el.getAttribute('data-count'), 10);
    const suffix = el.getAttribute('data-suffix') || '';
    const duration = 1800;
    const start = performance.now();

    function step(now) {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(eased * target);

      el.textContent = current + suffix;

      if (progress < 1) {
        requestAnimationFrame(step);
      }
    }

    requestAnimationFrame(step);
  }

  /* ==========================================================
     4. SMOOTH SCROLL
     ========================================================== */
  function initSmoothScroll() {
    const scrollBtn = document.querySelector('.ford-hero__scroll');
    if (!scrollBtn) return;

    scrollBtn.addEventListener('click', (e) => {
      e.preventDefault();
      const target = document.querySelector('.ford-stats') || document.querySelector('.ford-models');
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }

  /* ==========================================================
     5. HERO TITLE TRIGGER
     ========================================================== */
  function initHeroTitle() {
    const title = document.querySelector('.ford-hero__title');
    if (!title) return;

    setTimeout(() => {
      title.classList.add('is-visible');
    }, 300);
  }

  /* ==========================================================
     INIT
     ========================================================== */
  function init() {
    const canvas = document.getElementById('fordParticles');
    if (canvas) {
      new ForgeSparks(canvas);
    }

    initHeroTitle();
    initScrollAnimations();
    initCounters();
    initSmoothScroll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
